import React, { useState } from "react";
import { Plan } from "../@types";
import {
  PlanStarRatingRow,
  PlanStarRatingList,
  PlanStarRatingListItem,
} from ".";
import { getStarRating } from "../helpers/starRatingHelpers";
import { useTranslate } from "../helpers/intlHooks";
import { sendTealiumEvent } from "../helpers";

const DrugPlanStarRatings: React.FunctionComponent<{ plan: Plan }> = ({
  plan,
}) => {
  const t = useTranslate();
  const [drugPlanExpanded, setDrugPlanExpanded] = useState(false);
  const [customerServiceExpanded, setCustomerServiceExpanded] = useState(false);
  const [complaintsExpanded, setComplaintsExpanded] = useState(false);
  const [memberExperienceExpanded, setMemberExperienceExpanded] = useState(
    false
  );
  const [safetyExpanded, setSafetyExpanded] = useState(false);

  const toggleDrugPlanExpanded = (): void => {
    setDrugPlanExpanded(!drugPlanExpanded);

    if (!drugPlanExpanded) {
      setCustomerServiceExpanded(false);
      setComplaintsExpanded(false);
      setMemberExperienceExpanded(false);
      setComplaintsExpanded(false);
      setSafetyExpanded(false);

      sendTealiumEvent(
        "Plan Details - Star Ratings",
        "Toggle - Drug Plan Star Ratings"
      );
    }
  };
  return (
    <div className="ds-u-margin-bottom--2">
      <PlanStarRatingRow
        label={t("plan_stars.drug.star_ratings")}
        open={drugPlanExpanded}
        onClick={toggleDrugPlanExpanded}
        color="green"
        size="large"
        summary={t("plan_stars.drug.summary")}
        starRating={getStarRating(plan, "STAR_DRUG_PLAN_RATING")}
        ariaLabel={t("star_rating.drug_plan_aria")}
        plan={plan}
      >
        {/* Customer Service */}
        <PlanStarRatingRow
          label={t("plan_stars.drug.customer_service")}
          open={customerServiceExpanded}
          starRating={getStarRating(plan, "STAR_DRUG_CUSTOMER_SERVICE_RATING")}
          onClick={(): void => {
            setCustomerServiceExpanded(!customerServiceExpanded);
            if (!customerServiceExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan customer service"
              );
            }
          }}
          plan={plan}
        >
          <PlanStarRatingList>
            <PlanStarRatingListItem
              label={t("plan_stars.drug.customer_service.tty")}
              starRating={getStarRating(plan, "STAR_DRUG_TTY_AND_FL_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.customer_service.timely_decisions")}
              starRating={getStarRating(plan, "STAR_DRUG_APPEALS_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.customer_service.fair_appeals")}
              starRating={getStarRating(plan, "STAR_DRUG_APPEALS_FAIR_RATING")}
              planYear={plan.contract_year}
            />
          </PlanStarRatingList>
        </PlanStarRatingRow>

        {/* Member Complaints */}
        <PlanStarRatingRow
          label={t("plan_stars.drug.complaints")}
          open={complaintsExpanded}
          starRating={getStarRating(plan, "STAR_DRUG_COMPLAINTS_RATING")}
          onClick={(): void => {
            setComplaintsExpanded(!complaintsExpanded);
            if (!complaintsExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan member complaints"
              );
            }
          }}
          plan={plan}
        >
          <PlanStarRatingList>
            <PlanStarRatingListItem
              label={t("plan_stars.drug.complaints.complaints")}
              starRating={getStarRating(
                plan,
                "STAR_DRUG_PLAN_COMPLAINTS_RATING"
              )}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.complaints.leaving")}
              starRating={getStarRating(
                plan,
                "STAR_DRUG_MEMBERS_LEAVING_RATING"
              )}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.complaints.improvement")}
              starRating={getStarRating(plan, "STAR_DRUG_IMPROVEMENT_RATING")}
              planYear={plan.contract_year}
            />
          </PlanStarRatingList>
        </PlanStarRatingRow>

        {/* Member Experience */}
        <PlanStarRatingRow
          label={t("plan_stars.drug.experience")}
          open={memberExperienceExpanded}
          starRating={getStarRating(plan, "STAR_DRUG_MEMBER_EXP_RATING")}
          onClick={(): void => {
            setMemberExperienceExpanded(!memberExperienceExpanded);
            if (!memberExperienceExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan member experience"
              );
            }
          }}
          plan={plan}
        >
          <PlanStarRatingList>
            <PlanStarRatingListItem
              label={t("plan_stars.drug.experience.rating")}
              starRating={getStarRating(plan, "STAR_DRUG_PLAN_MEMBER_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.experience.prescription")}
              starRating={getStarRating(plan, "STAR_PRESCRIPTION_RATING")}
              planYear={plan.contract_year}
            />
          </PlanStarRatingList>
        </PlanStarRatingRow>

        {/* Drufy Safety and Pricing */}
        <PlanStarRatingRow
          label={t("plan_stars.drug.safety")}
          open={safetyExpanded}
          starRating={getStarRating(plan, "STAR_PROVIDES_PRICING_RATING")}
          onClick={(): void => {
            setSafetyExpanded(!safetyExpanded);
            if (!safetyExpanded) {
              sendTealiumEvent(
                "Plan Details - Star Ratings",
                "Toggle - Drug Plan safety and pricing"
              );
            }
          }}
          plan={plan}
        >
          <PlanStarRatingList>
            <PlanStarRatingListItem
              label={t("plan_stars.drug.safety.pricing")}
              starRating={getStarRating(plan, "STAR_DRUG_ACCURACY_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.safety.diabetes")}
              starRating={getStarRating(plan, "STAR_DIABETES_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.safety.blood_pressure")}
              starRating={getStarRating(plan, "STAR_BLOOD_PRESSURE_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.safety.cholesterol")}
              starRating={getStarRating(plan, "STAR_CHOLESTEROL_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.safety.pharmacist")}
              starRating={getStarRating(plan, "STAR_PHARMACIST_RATING")}
              planYear={plan.contract_year}
            />
            <PlanStarRatingListItem
              label={t("plan_stars.drug.safety.diabetes_cholesterol")}
              starRating={getStarRating(
                plan,
                "STAR_DIABETES_CHOLESTEROL_RATING"
              )}
              planYear={plan.contract_year}
            />
          </PlanStarRatingList>
        </PlanStarRatingRow>
      </PlanStarRatingRow>
    </div>
  );
};

export default DrugPlanStarRatings;
